import type { TimelinePhase } from '../../hooks/useTimeline';
import type { PhaseTimelineItem } from './AttackPhaseTimeline';

/**
 * F5 — pure bridge from the score-service kill-chain timeline
 * (`TimelinePhase`, produced by `useRangeTimeline`) to the presentational
 * `PhaseTimelineItem` shape the `AttackPhaseTimeline` stepper renders.
 *
 * The item `id` MUST stay the raw `phaseId` so the instructor Start button
 * (`onStart(phase.id)` → `startPhase({ rangeId, phaseId })`) dispatches the
 * correct phase. `detected` + `detectLatencySeconds` are carried through so the
 * timeline renders "detected · MTTD" (or "undetected · coverage gap").
 */

/** "admin_access" → "Admin Access". */
function humanize(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function phaseName(p: TimelinePhase): string {
  const base = p.phaseId ?? p.technique ?? 'phase';
  const label = humanize(base);
  // Append the MITRE technique only when it's a distinct identifier from the
  // phase id (avoids "T1595 · T1595").
  return p.technique && p.phaseId ? `${label} · ${p.technique}` : label;
}

export function timelineToPhaseItems(
  timeline: ReadonlyArray<TimelinePhase>,
): PhaseTimelineItem[] {
  return timeline.map((p, index) => {
    const item: PhaseTimelineItem = {
      id: p.phaseId ?? `phase-${index}`,
      name: phaseName(p),
      status: p.startedAt ? 'completed' : 'pending',
      detected: p.detected,
      detectLatencySeconds: p.detectLatencySeconds,
    };
    // exactOptionalPropertyTypes: only set startedAt when we actually have one.
    if (p.startedAt) item.startedAt = p.startedAt;
    return item;
  });
}
